import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { captchaToken } from '../../auth/captcha';
import { ensureOk, useAuthOptions } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { newPasswordSchema } from './password-schema';

const schema = z.object({
  name: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
  email: z.string().trim().min(1, 'validation.required').pipe(z.email('validation.email')),
  password: newPasswordSchema,
});
type Values = z.infer<typeof schema>;

export function RegisterPage() {
  const { t, locale } = useI18n();
  const options = useAuthOptions();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const register = useMutation({
    mutationFn: async (values: Values) => {
      ensureOk(
        await api.POST('/api/v1/auth/register', {
          body: {
            ...values,
            // Язык интерфейса — язык писем (docs/architecture.md §7.2).
            locale,
            captchaToken: await captchaToken(options.data?.recaptchaSiteKey ?? null, 'register'),
          },
        }),
      );
    },
  });

  if (register.isSuccess) {
    return (
      <AuthLayout title={t('auth.register.checkMailTitle')}>
        <p className="text-sm">{t('auth.register.checkMail')}</p>
        <AuthLinks>
          <Link to="/login">{t('auth.toLogin')}</Link>
        </AuthLinks>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.register.title')}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => register.mutate(values))}
        noValidate
      >
        <TextField
          control={form.control}
          name="name"
          label={t('auth.field.name')}
          autoComplete="name"
          autoFocus
        />
        <TextField
          control={form.control}
          name="email"
          type="email"
          label={t('auth.field.email')}
          autoComplete="email"
        />
        <TextField
          control={form.control}
          name="password"
          type="password"
          label={t('auth.field.password')}
          autoComplete="new-password"
          description={t('auth.field.passwordHint')}
        />
        {register.isError && <AuthError>{t(errorMessageKey(register.error))}</AuthError>}
        <Button type="submit" className="w-full" disabled={register.isPending}>
          {t('auth.register.submit')}
        </Button>
      </form>
      <AuthLinks>
        <Link to="/login">{t('auth.register.toLogin')}</Link>
      </AuthLinks>
    </AuthLayout>
  );
}
